import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { Moon, Sun } from 'lucide-react';
import { Button } from '../ui/button';
import '../../styles/painel-movimento.css';

export type Appearance = 'mineral' | 'grafite';
const COOKIE = 'pieltec_appearance';
const CONSULTA_ESCURO = '(prefers-color-scheme: dark)';
const DURACAO_TRANSICAO_MS = 240;

/** Escolha salva em cookie; null quando a pessoa ainda não escolheu (ou o valor é desconhecido). */
export function readAppearance(cookie: string): Appearance | null {
  for (const part of cookie.split(';')) {
    const texto = part.trim();
    if (texto === `${COOKIE}=grafite`) return 'grafite';
    if (texto === `${COOKIE}=mineral`) return 'mineral';
  }
  return null;
}

/** Sem escolha salva o tema segue o sistema (D-21); a escolha salva vence o sistema. */
export function resolverAparencia(salva: Appearance | null, prefereEscuro: boolean): Appearance {
  return salva ?? (prefereEscuro ? 'grafite' : 'mineral');
}

function sistemaPrefereEscuro(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(CONSULTA_ESCURO).matches;
}

function leituraDoCookie(): Appearance | null {
  return typeof document === 'undefined' ? null : readAppearance(document.cookie);
}

function movimentoReduzido(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

const AppearanceContext = createContext<{ theme: Appearance; toggle: () => void }>({ theme: 'mineral', toggle: () => undefined });

export function AppearanceProvider({ children }: { readonly children: ReactNode }): ReactElement {
  // Resolvido no inicializador (antes da primeira pintura): cookie vence; sem cookie, o sistema decide.
  const [theme, setTheme] = useState<Appearance>(() => resolverAparencia(leituraDoCookie(), sistemaPrefereEscuro()));
  const [escolhido, setEscolhido] = useState<boolean>(() => leituraDoCookie() !== null);
  const temporizador = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useLayoutEffect(() => {
    document.documentElement.dataset.appearance = theme;
    document.documentElement.style.colorScheme = theme === 'grafite' ? 'dark' : 'light';
  }, [theme]);

  // Enquanto a pessoa não escolher, acompanha a mudança do sistema.
  useEffect(() => {
    if (escolhido || typeof window.matchMedia !== 'function') return undefined;
    const consulta = window.matchMedia(CONSULTA_ESCURO);
    const aoMudar = (): void => setTheme(resolverAparencia(null, consulta.matches));
    consulta.addEventListener('change', aoMudar);
    return () => consulta.removeEventListener('change', aoMudar);
  }, [escolhido]);

  useEffect(() => () => clearTimeout(temporizador.current), []);

  function toggle(): void {
    const next: Appearance = theme === 'mineral' ? 'grafite' : 'mineral';
    document.cookie = `${COOKIE}=${next}; Path=/; Max-Age=31536000; SameSite=Lax${window.location.protocol === 'https:' ? '; Secure' : ''}`;
    if (!movimentoReduzido()) {
      // Transição suave só no instante da troca (U-29): a classe sai após 240 ms e nunca existe no carregamento.
      const raiz = document.documentElement;
      raiz.classList.add('theme-transition');
      clearTimeout(temporizador.current);
      temporizador.current = setTimeout(() => raiz.classList.remove('theme-transition'), DURACAO_TRANSICAO_MS);
    }
    setEscolhido(true);
    setTheme(next);
  }
  return <AppearanceContext.Provider value={{ theme, toggle }}>{children}</AppearanceContext.Provider>;
}

/** Tema atual e a troca, para quem não é o botão da topbar (ex.: o item do menu do avatar). */
export function useAppearance(): { theme: Appearance; toggle: () => void } {
  return useContext(AppearanceContext);
}

export function AppearanceToggle(): ReactElement {
  const { theme, toggle } = useContext(AppearanceContext);
  const label = theme === 'mineral' ? 'Ativar tema escuro' : 'Ativar tema claro';
  return <Button type="button" variant="ghost" size="icon" onClick={toggle} aria-label={label} title={label} className="appearance-toggle">
    {theme === 'mineral' ? <Moon size={18} aria-hidden="true" /> : <Sun size={18} aria-hidden="true" />}
  </Button>;
}
